import { Box, Grid } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import NoData from "../../../components/UI/NoData";
import { ITicketAutomationReceivedRuleAllowedAction } from "../../../services/tasySchedMenu.service";
import Table from "../../../components/UI/Table/Table";
import { ISchedule, IScheduleCheckInRule } from "../model";
import { Calendar } from "../Schedule/Calendar";
import Type from "../Schedule/Type";
import { CheckInButton } from "./CheckInButton/CheckInButton";
import { getScheduleDescriptionLines } from "./scheduleDescription";

interface IMySchedulesTableProps {
  data: ISchedule[];
  checkInRule: IScheduleCheckInRule;
  basePath?: string;
  allowedAction?: ITicketAutomationReceivedRuleAllowedAction | null;
}

const TRASNLATION_PATH = "page.schedules.mySchedules.";

const NewMySchedulesTable: React.FC<IMySchedulesTableProps> = ({
  data,
  checkInRule,
  basePath = "/schedule",
  allowedAction,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const columns: GridColDef<ISchedule>[] = [
    {
      field: "calendarDate",
      headerName: t(TRASNLATION_PATH + "date")!,
      flex: 1,
      minWidth: 200,
      renderCell: ({ row: { calendarStartDate } }) => (
        <Calendar date={calendarStartDate} />
      ),
    },

    {
      field: "pacientName",
      headerName: t(TRASNLATION_PATH + "pacientName")!,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "descriptionSite",
      headerName: t(TRASNLATION_PATH + "descriptionSite")!,
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {getScheduleDescriptionLines(row, t).map((line, index) => (
            <Box
              key={index}
              sx={{
                whiteSpace: "wrap",
                lineHeight: "1.2",
              }}
            >
              {line}
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "calendarStatusName",
      headerName: t(TRASNLATION_PATH + "status")!,
    },
    {
      field: "tasySchedulingType",
      headerName: t(TRASNLATION_PATH + "tasySchedulingType")!,
      width: 30,
      renderCell: ({ row: { tasySchedulingType } }) => (
        <Type type={tasySchedulingType.code!} />
      ),
    },
  ];

  if (checkInRule && (basePath !== "/schedule-new" || allowedAction?.checkIn)) {
    columns.push({
      field: "checkIn",
      headerName: t(TRASNLATION_PATH + "checkin")!,
      flex: 1,
      renderCell: ({
        row: {
          encounterIdentifier,
          identifier,
          roomName,
          calendarStartDate,
          tasySchedulingType,
          calendarStatusCode,
        },
      }) => (
        <CheckInButton
          checkInRule={checkInRule}
          encounterIdentifier={encounterIdentifier}
          roomName={roomName}
          scheduleDate={calendarStartDate}
          identifier={identifier}
          scheduleTypeCode={tasySchedulingType.code!}
          calendarStatusCode={calendarStatusCode}
        />
      ),
    });
  }

  const onRowClick: GridEventListener<"rowClick"> = useCallback(
    ({ row }) => {
      navigate(
        `${basePath}/mySchedules/${row.tasySchedulingType.code}/${row.identifier}`,
        { state: { allowedAction } },
      );
    },
    [navigate, basePath, allowedAction],
  );

  return (
    <Box pb={2} height={"100%"} width={"99%"}>
      <Table
        columns={columns}
        rows={data.map((c) => ({ ...c, id: c.identifier }))}
        rowHeight={150}
        rowAction={onRowClick}
      />

      {data.length === 0 && (
        <Grid container mt={5}>
          <Grid item xs={12} sm={12}>
            <NoData />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default memo(NewMySchedulesTable);
